<template>
	<header v-if="!isAuth && !isFullPageContent && !isErrorPage" v-cloak id="header" :class="['header', 'minified-sidebar', whichSection]">
		<header-partial />
	</header>

	<aside v-if="!isFullPageContent && !isErrorPage" v-cloak id="notification" class="notification">
		<notification-partial />
	</aside>

	<aside v-if="!isAuth && !isFullPageContent && !isErrorPage" v-cloak id="sidebar" :class="['sidebar', 'minified-sidebar', whichSection]">
		<sidebar-partial />
	</aside>

	<main
		v-cloak
		id="content"
		class="content minified-sidebar"
		:class="{'auth-page': isAuth, 'not-found' : isNotFound, 'action-bar-active' : isActionBarActive, 'full-page-content': isFullPageContent}"
	>

		<div v-if="!isFullPageContent" class="breadcrumbs">
			<!-- BACK LINK HERE -->
			<router-link v-if="show_back_button" :to="back_button_path" class="back-link">
				<fa class="header-icon" :icon="['fas', 'arrow-left']" />
				<span v-text="back_button_text"></span>
			</router-link>
		</div>

		<router-view v-slot="{ Component }" :key="getRoutePath">
			<transition name="fade" mode="out-in">
				<div :key="$route.fullPath">
					<component :is="Component" />
				</div>
			</transition>
		</router-view>
	</main>
</template>

<script lang="ts">
	import { Options, Vue } from 'vue-class-component';
	import { watch, WatchStopHandle } from 'vue';
	import { HttpHelper } from './helper/http';
	import HeaderPartial from './partials/Header.vue';
	import SidebarPartial from './partials/Sidebar.vue';
	import NotificationPartial from './partials/Notification.vue';
	import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';

	@Options({
		name: 'Application',
		inject: ['http'],
		components: { HeaderPartial, SidebarPartial, NotificationPartial },
	})
	export default class App extends Vue {
		public http!: HttpHelper;
		public routeQueryWatcher?: WatchStopHandle;
		public show_back_button = false;
		public back_button_text = '';
		public back_button_path = '';

		public processBackButton(to: RouteLocationNormalized): void {
			const pathParts: Array<string> = to.path.split('/');
			if (to.path.indexOf('/create/application') > -1 && pathParts.length > 5) {
				this.show_back_button = true;
				this.back_button_path = `/create/applications/${pathParts[3]}/environments`;
				this.back_button_text = 'To App';
			} else if (to.path.indexOf('/create/application') > -1 && pathParts.length > 4) {
				this.show_back_button = true;
				this.back_button_path = '/create/applications';
				this.back_button_text = 'To Apps List';
			} else if (to.path.indexOf('/ai/model') > -1 && pathParts.length >= 4) {
				this.show_back_button = true;
				this.back_button_path = '/ai/models';
				this.back_button_text = 'To AI Models';
			} else if (to.path.indexOf('/ai/knowledge-management/local/collection/') > -1 && pathParts.length >= 5) {
				this.show_back_button = true;
				this.back_button_path = '/ai/knowledge-management/local/collections';
				this.back_button_text = 'To Knowledge Management';
			} else if (to.path.indexOf('/ai/knowledge-management/dynamic/group/') > -1 && pathParts.length >= 5) {
				this.show_back_button = true;
				this.back_button_path = '/ai/knowledge-management/dynamic/groups';
				this.back_button_text = 'To Groups';
			} else {
				this.show_back_button = false;
			}
		}

		public get whichSection(): string {
			if (/^\/$/.test(this.$route.path)) {
				return 'dashboard';
			} else if (this.$route.path.startsWith('/create/')) {
				return 'create';
			} else if (this.$route.path.startsWith('/rpa/')) {
				return 'rpa';
			} else if (this.$route.path.startsWith('/ai/')) {
				return 'ai';
			} else if (this.$route.path.startsWith('/converse-cx/')) {
				return 'converse';
			} else if (this.$route.path.startsWith('/spark/')) {
				return 'spark';
			} else if (this.$route.path.startsWith('/idp/')) {
				return 'idp';
			}
			return 'controller';
		}

		public get getRoutePath(): string {
			const parts: Array<string> = this.$route.path.split('/').filter((part: string) => part !== '' ? part : undefined);
			if (parts[0] === 'create') {
				if (parts.length > 4) {
					return `${parts[2]}_${parts[3]}`;
				} else if (parts.length > 3) {
					return `app-${parts[2]}`;
				} else {
					return parts.join('_');
				}
			} else if (parts[0] === 'settings') {
				return 'settings';
			} else {
				return parts.join('_');
			}
		}

		public get isNotFound(): boolean {
			return this.$route.name === 'NotFound';
		}

		public get isAuth(): boolean {
			if (this.$route.path.indexOf('/auth/') > -1) return true;
			return false;
		}

		public get isFullPageContent(): boolean {
			if (this.$route.path.indexOf('/confusion-matrix/') > -1) return true;
			return false;
		}

		public get isErrorPage(): boolean {
			if (this.$route.path.includes('/page404')) return true;
			if (this.$route.path.toLowerCase().includes('/forbidden')) return true;
			return false;
		}

		public get isActionBarActive(): boolean {
			const routeParts = this.$route.path.split('/');
			if (routeParts[1].trim() === 'create' && routeParts[2].trim() === 'application') {
				if (routeParts[5].trim() === 'overview' || routeParts[5].trim() === 'users' || routeParts[5].trim() === 'resources') {
					return true;
				}
			}
			return false;
		}

		public get isLoggedIn(): boolean {
			return this.$store.state.account.isLoggedIn;
		}

		public hideLoader(): void {
			setTimeout(() => {
				const loader = document.getElementById('loader');
				if (loader) loader.style.display = 'none';
			}, 250);
		}

		public async loadConfiguration(): Promise<void> {
			if (!this.isLoggedIn) return;
			const response = await this.http.get('/api/configuration', { autoRefresh: 'auto' });
			if (response.status === 200) {
				const result = await response.json();
				this.$store.dispatch('setConfiguration', result);
			}
		}

		public async mounted(): Promise<void> {

			this.$router.beforeEach((to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
				this.processBackButton(to);
				next();
			});

			await this.loadConfiguration();

			setInterval(() => {
				this.loadConfiguration();
			}, 60000);

			this.hideLoader();

			this.routeQueryWatcher = watch(() => this.$route.query, async () => {
				if (this.$route.query.e) {
					const message = await this.getErrorMessage();

					if (message.length <= 0) return;

					this.$store.dispatch('notification/add', {
						type: 'error',
						message,
					});
				}
			});
		}

		public async getErrorMessage(): Promise<string> {
			const response = await this.http.post('/api/error_message', { uuid: this.$route.query.e });
			if (response.status === 200) {
				const result = await response.json(); ;

				return result?.error_message;
			}

			return '';
		}

	}
</script>

	<style lang="scss">
	@import './assets/styles/base';
	@import './assets/styles/base/_core';
	@import './assets/styles/base/_reset';
	@import './assets/styles/base/_normalise';
	@import './assets/styles/partials/layout';
	@import './assets/styles/modules';

	.breadcrumbs {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 1.5rem;
	}

</style>
